import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import '../assets/css/common.css';
import { qualificationService } from '../services/qualificationService';
import { toWareki } from '../utils/wareki';

const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'application/pdf'];

const EMPTY_FORM = {
  name: '',
  acquiredOn: '',
  certificateNumber: '',
  note: '',
  attachment: null,
  attachmentName: '',
  removeAttachment: false
};

function Qualifications() {
  const [qualifications, setQualifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [errors, setErrors] = useState([]);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // ファイル入力は非制御要素のため、key を変えて表示をリセットする
  const [fileInputKey, setFileInputKey] = useState(0);

  const fetchQualifications = useCallback(async () => {
    try {
      const data = await qualificationService.getMyQualifications();
      setQualifications(data.qualifications || []);
    } catch (err) {
      setErrors(['資格情報の取得に失敗しました。時間をおいて再度お試しください。']);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQualifications();
  }, [fetchQualifications]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) {
      setForm((prev) => ({ ...prev, attachment: null, attachmentName: '' }));
      return;
    }
    if (!ALLOWED_TYPES.includes(file.type)) {
      setErrors(['証跡はJPEG・PNG・PDFのいずれかを選択してください。']);
      e.target.value = '';
      return;
    }
    if (file.size > MAX_ATTACHMENT_BYTES) {
      setErrors(['証跡ファイルは5MB以内にしてください。']);
      e.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setErrors([]);
      setForm((prev) => ({
        ...prev,
        attachment: reader.result,
        attachmentName: file.name,
        removeAttachment: false
      }));
    };
    reader.onerror = () => setErrors(['ファイルの読み込みに失敗しました。']);
    reader.readAsDataURL(file);
  };

  const validate = () => {
    const found = [];
    if (!form.name.trim()) found.push('資格名は必須です。');
    if (!form.acquiredOn) found.push('取得年月日は必須です。');
    else if (new Date(form.acquiredOn) > new Date()) found.push('取得年月日に未来の日付は指定できません。');
    if (form.note && form.note.length > 500) found.push('備考は500文字以内で入力してください。');
    return found;
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setFileInputKey((key) => key + 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const found = validate();
    if (found.length) {
      setErrors(found);
      setMessage('');
      return;
    }

    setSubmitting(true);
    setErrors([]);
    try {
      const payload = {
        name: form.name.trim(),
        acquiredOn: form.acquiredOn,
        certificateNumber: form.certificateNumber || null,
        note: form.note || null,
        attachment: form.attachment || undefined,
        attachmentName: form.attachmentName || undefined
      };

      if (editingId) {
        await qualificationService.updateQualification(editingId, {
          ...payload,
          removeAttachment: form.removeAttachment
        });
        setMessage('資格取得報告を更新しました。');
      } else {
        await qualificationService.createQualification(payload);
        setMessage('資格取得報告を登録しました。');
      }

      resetForm();
      await fetchQualifications();
    } catch (err) {
      setErrors([err?.data?.error || '保存に失敗しました。時間をおいて再度お試しください。']);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (qualification) => {
    setEditingId(qualification.id);
    setFileInputKey((key) => key + 1);
    setMessage('');
    setErrors([]);
    setForm({
      name: qualification.name,
      acquiredOn: qualification.acquiredOn || '',
      certificateNumber: qualification.certificateNumber || '',
      note: qualification.note || '',
      attachment: null,
      attachmentName: qualification.hasAttachment ? qualification.attachmentName || '' : '',
      removeAttachment: false
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleViewAttachment = async (id) => {
    try {
      const data = await qualificationService.getAttachment(id);
      const win = window.open();
      if (!win) {
        setErrors(['ポップアップがブロックされました。ブラウザの設定をご確認ください。']);
        return;
      }
      if ((data.attachmentMime || '').startsWith('image/')) {
        win.document.write(
          `<title>${data.attachmentName || '証跡'}</title><img src="${data.attachment}" style="max-width:100%">`
        );
      } else {
        win.location.href = data.attachment;
      }
    } catch (err) {
      setErrors(['証跡の取得に失敗しました。']);
    }
  };

  return (
    <div className="container" style={{ maxWidth: 900, margin: '0 auto', padding: 24 }}>
      <h1 className="text-primary text-center mb-4">資格取得報告</h1>

      {errors.length > 0 && (
        <div className="alert alert-danger">
          {errors.map((error, index) => <div key={index}>{error}</div>)}
        </div>
      )}
      {message && <div className="alert alert-success">{message}</div>}

      <form onSubmit={handleSubmit} className="mb-4">
        <h2 className="mb-3">{editingId ? '資格取得報告の編集' : '新しい資格を報告する'}</h2>

        <div className="mb-3">
          <label className="form-label" htmlFor="qualification-name">資格名 <span className="text-danger">*</span></label>
          <input
            id="qualification-name"
            className="form-control"
            type="text"
            name="name"
            maxLength={255}
            value={form.name}
            onChange={handleChange}
            placeholder="例: 基本情報技術者試験"
          />
        </div>

        <div className="mb-3">
          <label className="form-label" htmlFor="qualification-acquired-on">取得年月日 <span className="text-danger">*</span></label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <input
              id="qualification-acquired-on"
              className="form-control"
              style={{ maxWidth: 220 }}
              type="date"
              name="acquiredOn"
              value={form.acquiredOn}
              onChange={handleChange}
            />
            {form.acquiredOn && (
              <span className="text-muted" style={{ whiteSpace: 'nowrap' }}>{toWareki(form.acquiredOn)}</span>
            )}
          </div>
        </div>

        <div className="mb-3">
          <label className="form-label" htmlFor="qualification-number">登録番号・認定番号</label>
          <input
            id="qualification-number"
            className="form-control"
            type="text"
            name="certificateNumber"
            maxLength={100}
            value={form.certificateNumber}
            onChange={handleChange}
          />
        </div>

        <div className="mb-3">
          <label className="form-label" htmlFor="qualification-attachment">証跡（合格証・認定証）</label>
          <input
            key={fileInputKey}
            id="qualification-attachment"
            className="form-control"
            type="file"
            accept="image/jpeg,image/png,application/pdf"
            onChange={handleFileChange}
          />
          <small className="text-muted">JPEG・PNG・PDF、5MBまで。</small>
          {form.attachmentName && (
            <div className="mt-1 text-small">
              選択中: {form.attachmentName}
              {editingId && (
                <button
                  type="button"
                  className="btn btn-link btn-sm"
                  onClick={() => {
                    setForm((prev) => ({ ...prev, attachment: null, attachmentName: '', removeAttachment: true }));
                    setFileInputKey((key) => key + 1);
                  }}
                >
                  証跡を削除する
                </button>
              )}
            </div>
          )}
        </div>

        <div className="mb-3">
          <label className="form-label" htmlFor="qualification-note">備考</label>
          <textarea
            id="qualification-note"
            className="form-control"
            name="note"
            rows={3}
            maxLength={500}
            value={form.note}
            onChange={handleChange}
          />
        </div>

        <div style={{ display: 'flex', gap: 12 }}>
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? '保存中...' : editingId ? '更新する' : '登録する'}
          </button>
          {editingId && (
            <button type="button" className="btn btn-secondary" onClick={resetForm}>
              編集をやめる
            </button>
          )}
        </div>
      </form>

      <h2 className="mb-3">報告済みの資格</h2>
      {loading ? (
        <p className="text-muted">読み込み中...</p>
      ) : qualifications.length === 0 ? (
        <p className="text-muted">まだ資格の報告はありません。</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>資格名</th>
              <th>取得年月日</th>
              <th>登録番号</th>
              <th>証跡</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {qualifications.map((qualification) => (
              <tr key={qualification.id}>
                <td>
                  <div>{qualification.name}</div>
                  {qualification.note && <div className="text-small text-muted">{qualification.note}</div>}
                </td>
                <td>
                  <div>{qualification.acquiredOn}</div>
                  <div className="text-small text-muted" style={{ whiteSpace: 'nowrap' }}>{toWareki(qualification.acquiredOn)}</div>
                </td>
                <td>{qualification.certificateNumber || '-'}</td>
                <td>
                  {qualification.hasAttachment ? (
                    <button className="btn btn-link btn-sm p-0" onClick={() => handleViewAttachment(qualification.id)}>
                      {qualification.attachmentName || '証跡を表示'}
                    </button>
                  ) : (
                    <span className="text-muted">なし</span>
                  )}
                </td>
                <td>
                  <button className="btn btn-secondary btn-sm" onClick={() => handleEdit(qualification)}>編集</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <p className="text-muted text-small mt-3">
        ※ 報告済みの資格を取り消す場合は管理者にご連絡ください。
      </p>

      <div className="mt-4">
        <Link to="/" className="btn btn-secondary">マイページに戻る</Link>
      </div>
    </div>
  );
}

export default Qualifications;
